import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { LocationOn as LocationOnIcon } from "@mui/icons-material";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { ReactComponent as EditIcon } from "../../../../Assets/img/icons/edit-icon-figma.svg";
import Modal from "@mui/material/Modal";
import BusinessIcon from "@mui/icons-material/Business";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { getGoogleplaces } from "../../../../Redux/helpers/JobsApi";
import { useForm, Controller } from "react-hook-form";
import JDTextEditor from "./JDTextEditor";
import DOMPurify from 'dompurify';

const PostingJobModal = ({
  open,
  onClose,
  currentStep,
  hiringData,
  companyLogo,
  menuOptions,
  hiringInputValue,
  localGeneratedSkills,
  selectedChips,
  isLoadingSkills,
  isLoadingDescription,
  handleHiringModalClose,
  handleHiringInputChange,
  // handleKeyDown,
  handleHiringAddChip,
  handleChipToggle,
  handleHiringChipDelete,
  handleEditSkillClick,
  handleEditResponseClick,
  handleBack,
  handleNext,
  handleClose,
  handleCancel,
  handleHiringSubmit,
  setHiringInputValue,
  setCurrentStep,
  fieldErrors,
  isEditing = false,
}) => {
  const dispatch = useDispatch();
  const [locations, setLocations] = useState([]);
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  // console.log("NNNNN", hiringData)

  const onChangeLocation = async (input) => {
    try {
      // Input validation
      if (!input || typeof input !== "string") {
        setLocations([]);
        return;
      }

      // Only fetch if input is longer than 2 characters
      if (input.length > 2) {
        // Show loading state
        setLocations([]);

        const response = await getGoogleplaces(input);

        // Handle different response formats
        if (Array.isArray(response)) {
          // Handle simple string array response
          setLocations(
            response.map((place) => ({
              description: place,
              placeId: place,
            }))
          );
        } else if (response?.predictions) {
          // Handle Google Places API response format
          const validLocations = response.predictions
            .filter((place) => place?.description)
            .map((place) => ({
              description: place.description,
              placeId: place.place_id || place.description,
            }));
          setLocations(validLocations);
        } else {
          console.error("Invalid response format:", response);
          setLocations([]);
        }

        console.log(`Found ${locations.length} locations for query: ${input}`);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  const getModalTitle = () => {
    if (currentStep === 3) return "Preview";
    return isEditing ? "Edit Job" : "Post New Job";
  };

  const getSubmitButtonText = () => {
    if (currentStep < 3) return "Next";
    return isEditing ? "Save Changes" : "Post Job";
  };

  const formatSalary = (salary) => {
    if (!salary) return "Not Specified";

    if (/^\d+(\.\d+)?k$/.test(salary)) {
      return salary; // If it's in 'k' format, return the salary as is
    }

    // Check if the salary is a range (e.g., "10000-20000")
    const salaryParts = salary.split("-");

    if (salaryParts.length === 2) {
      const minSalary = parseInt(salaryParts[0].replace(/,/g, ""));
      const maxSalary = parseInt(salaryParts[1].replace(/,/g, ""));

      // Check if both values are valid numbers
      if (isNaN(minSalary) || isNaN(maxSalary)) {
        return "Not Specified"; // If either value is NaN, return "Not Specified"
      }

      // Convert salary range to 'k' format
      return `${minSalary / 1000}k - ${maxSalary / 1000}k`;
    } else {
      const salaryInK = parseInt(salary.replace(/,/g, ""));

      // If the salary is not a valid number, return "Not Specified"
      if (isNaN(salaryInK)) {
        return "Not Specified";
      }

      return `${salaryInK / 1000}k`; // If it's a single salary, convert to 'k' format
    }
  };

  const areAllFieldsFilled = () => {
    const requiredFields = [
      "role",
      "company_name",
      "company_url",
      "company_location",
      "dept_type",
      "emp_type",
      "edu_level",
      "experience",
      "salary",
      "location",
      "mode",
      "no_of_rounds",
      "no_of_open_positions",

    ];

    if (currentStep === 3) {
      return hiringData?.job_description_md && hiringData.job_description_md.trim() !== "";
    }

    return requiredFields?.every(
      (field) =>
        hiringData[field] && hiringData[field]?.toString().trim() !== ""
    );
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues:
      hiringData ||
      {
        // Add your default values here
      },
  });

  const onSubmit = (data) => {
    // Handle form submission
    handleHiringSubmit(data);
  };

  const safeRenderHTML = (html) => {
    if (!html) return null;

    // Basic sanitization - remove script tags
    const cleanHTML = html.replace(/<script.*?>.*?<\/script>/gi, "");

    return (
      <div
        dangerouslySetInnerHTML={{ __html: cleanHTML }}
        style={{
          maxHeight: "400px",
          overflow: "auto",
          marginTop: "16px",
          padding: "8px",
          border: "1px solid #f0f0f0",
          borderRadius: "4px",
        }}
      />
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          maxHeight: "80vh",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "1rem", fontWeight: "bold" }}
          >
            {getModalTitle()}
          </Typography>
          <IconButton onClick={handleHiringModalClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {/* Step 1 Content */}
          {currentStep === 1 && (
            <>
              {/* Profile Picture with Badge */}
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "8px",
                  p: 2,
                  mb: 2,
                  backgroundColor: "#F5F5F5",
                  textAlign: "center",
                  overflow: "hidden",
                  textTransform: "none",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 160,
                    height: 160,
                    mx: "auto",
                    mb: 1,
                    overflow: "hidden",
                  }}
                >
                  {hiringData?.company_logo ? (
                    <img
                      src={hiringData?.company_logo}
                      alt="Company Logo"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <BusinessIcon
                      sx={{
                        width: "100%",
                        height: "100%",
                        color: "#9e9e9e",
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Form Fields for Step 1 */}
              {[
                {
                  label: "Company Name",
                  value: hiringData?.company_name,

                  name: "company_name",
                  edit: true,
                },
                {
                  label: "Company Website",
                  value: hiringData?.company_url,

                  name: "company_url",
                  edit: true,
                },
                {
                  label: "Company location",
                  value: hiringData?.company_location,

                  name: "company_location",
                  edit: true,
                },
                {
                  label: "Job Role",
                  value: hiringData?.role,

                  name: "role",
                  edit: false,
                },
                {
                  label: "Job Location",
                  value: hiringData?.location,

                  name: "location",
                  edit: false,
                },
              ]?.map(({ label, value, name, edit }) => {
                if (label === "Job Location") {
                  return (
                    <Autocomplete
                      freeSolo
                      options={locations}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        return typeof option === "string"
                          ? option
                          : option.description || "";
                      }}
                      value={hiringData?.location || null}
                      onChange={(event, newValue) => {
                        // Create a synthetic event object
                        handleHiringInputChange({
                          target: {
                            name: "location",
                            value:
                              typeof newValue === "string"
                                ? newValue
                                : newValue?.description || "",
                          },
                          // Add preventDefault method
                          preventDefault: () => { },
                        });
                      }}
                      onInputChange={(event, newValue) => {
                        // Only call onChangeLocation if it's a real change event
                        if (event?.type === "change") {
                          onChangeLocation(newValue);
                        }
                      }}
                      filterOptions={(options, params) => {
                        const filtered = options.filter((option) => {
                          const value =
                            typeof option === "string"
                              ? option
                              : option.description;
                          return value
                            .toLowerCase()
                            .includes(params.inputValue.toLowerCase());
                        });
                        return filtered;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Job Location"
                          required
                          variant="outlined"
                          fullWidth
                          sx={{ mb: 2 }}
                          error={!!fieldErrors?.location}
                          helperText={fieldErrors?.location || ""}
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Typography variant="body2">
                            {typeof option === "string"
                              ? option
                              : option.description}
                          </Typography>
                        </li>
                      )}
                      noOptionsText="No locations found"
                      loading={locations.length === 0}
                      loadingText="Searching locations..."
                    />
                  );
                } else {
                  return (
                    <TextField
                      key={name}
                      label={label}
                      value={value}
                      disabled={edit}
                      name={name}
                      onChange={handleHiringInputChange}
                      // handleKeyDown={handleKeyDown}
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 2 }}
                      type="text"
                      required
                      // inputProps={{ style: { fontSize: "0.875rem" } }}
                      inputProps={{
                        style: { fontSize: "0.875rem" },
                        readOnly:
                          [
                            "company_name",
                            "company_url",
                            "company_location",
                          ].includes(name) && !!value, // Read-only if field is pre-filled
                      }}
                    />
                  );
                }
              })}
              {[
                {
                  label: "Department Type",
                  value: hiringData?.dept_type,
                  name: "dept_type",
                },
                {
                  label: "Employment Type",
                  value: hiringData?.emp_type,
                  name: "emp_type",
                },
                {
                  label: "Education Type",
                  value: hiringData?.edu_level,
                  name: "edu_level",
                },
                {
                  label: "Experience Range",
                  value: hiringData?.experience,
                  name: "experience",
                },
                {
                  label: "Salary Range",
                  value: hiringData?.salary,
                  name: "salary",
                },

                {
                  label: "Work Mode",
                  value: hiringData?.mode,
                  name: "mode",
                },
              ]?.map(({ label, name }) => (
                <FormControl fullWidth sx={{ mb: 2 }} key={name}>
                  <InputLabel sx={{ fontSize: "0.875rem" }}>
                    {label} <span style={{ color: "gray" }}>*</span>
                  </InputLabel>
                  <Select
                    name={name}
                    value={hiringData[name] || ""}
                    onChange={handleHiringInputChange}
                    label={label}
                    required
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {menuOptions[label]?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* {!hiringData[name] && (
      <Typography variant="caption" color="error">
        {label} is required.
      </Typography>
    )} */}
                </FormControl>
              ))}

              <FormControl fullWidth>
                <InputLabel>
                  Number of Interview Rounds{" "}
                  <span style={{ color: "gray" }}>*</span>{" "}
                </InputLabel>
                <Select
                  name="no_of_rounds"
                  value={hiringData?.no_of_rounds}
                  onChange={handleHiringInputChange}
                  label="Number of Interview Rounds"
                  error={fieldErrors?.no_of_rounds}
                >
                  {[...Array(10)]?.map((_, index) => (
                    <MenuItem key={index + 1} value={index + 1}>
                      {index + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                name="no_of_open_positions"
                value={hiringData?.no_of_open_positions ?? ""}
                onChange={(e) => {
                  const val = e.target.value;

                  // Allow empty field (for clearing input)
                  if (val === "") {
                    handleHiringInputChange(e);
                    return;
                  }

                  const value = parseInt(val, 10);

                  // Update only if valid number and within range
                  if (!isNaN(value) && value >= 1 && value <= Infinity) {
                    handleHiringInputChange(e);
                  }
                }}
                label="Number of Open Positions"
                type="text"
                required
                inputProps={{
                  inputMode: "numeric", // mobile-friendly numeric keyboard
                  pattern: "[0-9]*", // restrict to numbers
                }}
                error={fieldErrors?.no_of_open_positions}
                helperText={
                  fieldErrors?.no_of_open_positions
                    ? "Please enter a valid number (1–999)"
                    : ""
                }
              />
            </>
          )}

          {/* Step 2 Content */}
          {currentStep === 2 && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Choose or Add Skills & Tools
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "left",
                  width: "100%",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "600px",
                    marginBottom: 2,
                  }}
                >
                  <TextField
                    placeholder="Add a New Skill"
                    variant="outlined"
                    value={hiringInputValue}
                    onChange={(e) => setHiringInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && hiringInputValue.trim()) {
                        // Check if input is not entirely numeric
                        if (!/^\d+$/.test(hiringInputValue.trim())) {
                          handleHiringAddChip(hiringInputValue.trim());
                          e.preventDefault();
                          setHiringInputValue("");
                        }
                      }
                    }}
                    size="small"
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "25px",
                        paddingRight: "100px",
                      },
                    }}
                  />
                  <Chip
                    label="Add"
                    color="primary"
                    onClick={() => {
                      if (hiringInputValue.trim()) {
                        // Check if input is not entirely numeric
                        if (!/^\d+$/.test(hiringInputValue.trim())) {
                          handleHiringAddChip(hiringInputValue.trim());
                          setHiringInputValue("");
                        }
                      }
                    }}
                    sx={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      borderRadius: "20px",
                      height: "32px",
                      padding: "0 20px", // Increased padding for wider button
                      fontSize: "0.875rem",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginBottom: "50px",
                    justifyContent: "left",
                    width: "100%",
                    maxWidth: "800px",
                  }}
                >
                  {localGeneratedSkills?.map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      onClick={() => handleChipToggle(chip)}
                      onDelete={() => handleChipToggle(chip)}
                      deleteIcon={
                        selectedChips.includes(chip) ? (
                          <ClearIcon style={{ color: "red" }} />
                        ) : (
                          <AddCircleOutlinedIcon />
                        )
                      }
                      color={
                        selectedChips.includes(chip) ? "primary" : "default"
                      }
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Step 3 Content */}
          {currentStep === 3 && (
            <Box sx={{ padding: "20px", width: "100%" }}>
              {/* Header Section */}
              <Box
                display="flex"
                justifyContent="left"
                gap="30px"
                padding={"10px"}
                alignItems="center"
              >
                <img
                  width={"60px"}
                  height={"80px"}
                  src={
                    hiringData?.company_logo
                      ? hiringData?.company_logo
                      : "https://www.shutterstock.com/image-vector/image-icon-trendy-flat-style-600nw-643080895.jpg"
                  }
                  alt="Company Logo"
                />
                <Box>
                  <Typography variant="h5">{hiringData?.role}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hiringData?.company_name}
                  </Typography>
                </Box>
              </Box>

              {/* Job Details */}
              <Box
                display="flex"
                alignItems="center"
                gap="16px"
                mt={1}
                sx={{ paddingLeft: "10px" }}
              >
                {/* Location Section */}
                <Box display="flex" alignItems="center" gap="4px">
                  <LocationOnIcon sx={{ fontSize: 16, color: "gray" }} />
                  <Typography variant="body2">
                    {hiringData?.location}
                  </Typography>
                </Box>

                {/* Experience & Salary Section */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap="6px"
                  fontSize={"12px"}
                >
                  <HomeRepairServiceIcon sx={{ fontSize: 16, color: "gray" }} />
                  <Typography variant="body2">
                    {hiringData?.experience}
                  </Typography>
                  <CurrencyRupeeIcon sx={{ fontSize: 16, color: "gray" }} />
                  <Typography variant="body2">
                    {formatSalary(hiringData?.salary)}
                  </Typography>
                </Box>
              </Box>

              <hr />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  onClick={() => {
                    // Navigate to step 1 for basic details editing
                    setCurrentStep(1);
                  }}

                >
                  <EditIcon />
                </IconButton>
              </Box>

              {/* Job Description */}
              <Box bgcolor="white" borderRadius={2} p={3} mb={3}>
                {/* <Typography variant="h6">Job Description</Typography> */}
                <Typography variant="body2">
                  <strong>Role:</strong> {hiringData?.role}
                </Typography>
                <Typography variant="body2">
                  <strong>Department Type:</strong> {hiringData?.dept_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Employment Type:</strong> {hiringData?.emp_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Education:</strong> {hiringData?.edu_level}
                </Typography>
                <Typography variant="body2">
                  <strong>Mode:</strong> {hiringData?.mode}
                </Typography>
                <Typography variant="body2">
                  <strong>Key Skills:</strong>{" "}
                  {hiringData?.skills?.map((skill) => (
                    <Chip
                      key={skill}
                      label={skill}
                      size="small"
                      sx={{ margin: "2px" }}
                    />
                  ))}
                </Typography>
              </Box>

              {/* Roles & Responsibilities */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
              </Box>


              <JDTextEditor
                value={hiringData.job_description_md || ""}
                onChange={(html) => {
                  handleHiringInputChange({
                    target: {
                      name: "job_description_md",
                      value: html,
                    },
                  });
                }}
              />
              
              {/* </Box> */}

            </Box>
          )}

          {/* Step 4 Content */}
          {currentStep === 4 && (
            <>
              <Box display="flex" flexDirection="column" gap={2} mt={4}>
                <TextField
                  variant="outlined"
                  name="job_summary"
                  value={hiringData?.job_summary || ""}
                  onChange={handleHiringInputChange}
                  label="Job Summary"
                  multiline
                  rows={4}
                />
                <TextField
                  variant="outlined"
                  name="responsibility"
                  value={
                    Array.isArray(hiringData?.responsibility)
                      ? hiringData.responsibility.join("\n")
                      : hiringData?.responsibility || ""
                  }
                  onChange={handleHiringInputChange}
                  label="Roles & Responsibilities"
                  multiline
                  rows={6}
                  placeholder="• Responsibility 1&#10;• Responsibility 2"
                />
                <TextField
                  variant="outlined"
                  name="qualifications"
                  value={
                    Array.isArray(hiringData?.qualifications)
                      ? hiringData.qualifications.join("\n")
                      : hiringData?.qualifications || ""
                  }
                  onChange={handleHiringInputChange}
                  label="Qualifications"
                  multiline
                  rows={6}
                  placeholder="• Qualification 1&#10;• Qualification 2"
                />
                <TextField
                  variant="outlined"
                  name="perks"
                  value={
                    Array.isArray(hiringData?.perks)
                      ? hiringData.perks.join("\n")
                      : hiringData?.perks || ""
                  }
                  onChange={handleHiringInputChange}
                  label="Perks & Benefits"
                  multiline
                  rows={4}
                  placeholder="• Perk 1&#10;• Perk 2"
                  error={fieldErrors?.perks}
                  helperText={
                    fieldErrors?.perks ? "Please add at least one perk" : ""
                  }
                />
                <TextField
                  variant="outlined"
                  name="pre_requisites"
                  value={
                    Array.isArray(hiringData?.pre_requisites)
                      ? hiringData.pre_requisites.join("\n")
                      : hiringData?.pre_requisites || ""
                  }
                  onChange={handleHiringInputChange}
                  label="Pre-requisites"
                  multiline
                  rows={4}
                  placeholder="• Pre-requisite 1&#10;• Pre-requisite 2"
                  error={fieldErrors?.pre_requisites}
                  helperText={
                    fieldErrors?.pre_requisites
                      ? "Please add at least one pre-requisite"
                      : ""
                  }
                />
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {currentStep === 4 ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                sx={{ fontSize: "0.875rem" }}
                onClick={handleBack}
                disabled={isLoadingSkills || isLoadingDescription}
              >
                Back
              </Button>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep(3)} // Go back to preview
                  s
                >
                  Back to Preview
                </Button>
                <Button
                  variant="contained"
                  onClick={handleHiringSubmit}
                  disabled={!hiringData?.about_company?.trim()}
                >
                  {getSubmitButtonText()}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {currentStep > 1 ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ fontSize: "0.875rem" }}
                  onClick={handleBack}
                  disabled={isLoadingSkills || isLoadingDescription}
                >
                  Back
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ fontSize: "0.875rem" }}
                  onClick={handleHiringModalClose}
                  disabled={isLoadingSkills || isLoadingDescription}
                >
                  Cancel
                </Button>
              )}
              {currentStep < 3 ? (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ fontSize: "0.875rem" }}
                  onClick={handleNext}
                  disabled={
                    !areAllFieldsFilled() ||
                    isLoadingSkills ||
                    isLoadingDescription
                  }
                >
                  {!isEditing && (isLoadingSkills || isLoadingDescription) ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {currentStep === 1
                        ? "Generating Skills..."
                        : "Generating Description..."}
                    </Box>
                  ) : (
                    "Next"
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ fontSize: "0.875rem" }}
                  onClick={handleHiringSubmit}
                  disabled={
                    !areAllFieldsFilled() ||
                    isLoadingSkills ||
                    isLoadingDescription
                  }
                >
                  {getSubmitButtonText()}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PostingJobModal;
